type StatsValue =
  | string
  | number
  | boolean
  | { completed: number; total: number }
  | undefined;

export interface UserStats {
  coursesCompleted?: number;
  quizzesTaken?: number;
  materialsViewed?: number;
  courses?: { completed: number; total: number };
  quizzes?: { completed: number; total: number };
  materials?: { completed: number; total: number };
  [key: string]: StatsValue;
}

type UserValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | { [key: string]: unknown };

export interface UserData {
  id?: string | number;
  name?: string;
  email?: string;
  role?: string;
  aadhar?: string;
  aadhaarNumber?: string;
  cdc?: string;
  indianCdc?: string;
  rank?: string;
  rankDesignation?: string;
  ship?: string;
  currentShipName?: string;
  imo?: string;
  currentShipImo?: string;
  seafarerSid?: string;
  avatar?: string;
  profileImage?: string;
  age?: string | number;
  gender?: string;
  phone?: string;
  mobileNumber?: string;
  stats?: UserStats;
  createdAt?: string;
  updatedAt?: string;
  emailVerified?: boolean;
  lastLogin?: string | null;
  [key: string]: UserValue;
}

export interface NormalizedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  profileImage: string;
  aadhaarNumber: string;
  rankDesignation: string;
  currentShipName: string;
  currentShipImo: string;
  indianCdc: string;
  seafarerSid: string;
  age: string | number;
  gender: string;
  phone: string;
  stats: {
    courses: { completed: number; total: number };
    quizzes: { completed: number; total: number };
    materials: { completed: number; total: number };
  };
  createdAt: string;
  updatedAt: string;
  emailVerified: boolean;
  lastLogin: string | null;
  _raw: UserData;
  _sources: string[];
  [key: string]: unknown;
}

export interface UserDisplayInfo {
  name: string;
  email: string;
  role: string;
  profileImage: string;
  aadhaarNumber: string;
  rank: string;
  ship: string;
  imo: string;
  cdc: string;
  seafarerSid: string;
  mobileNumber: string;
  gender: string;
  age: string | number;
  phone: string;
  stats: {
    courses: number;
    quizzes: number;
    materials: number;
  };
}

/**
 * Normalizes user data from different sources into a consistent format
 * @param {UserData | null | undefined} userData - Raw user data from API or localStorage
 * @param {string} source - Source of the data ('login' | 'auth/me' | 'profile')
 * @returns {NormalizedUser | null} Normalized user data
 */
export function normalizeUserData(
  userData: UserData | null | undefined,
  source: string = "users/me"
): NormalizedUser | null {
  if (!userData) return null;

  const getString = (val: unknown, def = ""): string =>
    typeof val === "string" ? val : def;

  const getNumber = (val: unknown, def = 0): number =>
    typeof val === "number" ? val : def;

  const getAge = (val: unknown): string | number =>
    typeof val === "string" || typeof val === "number" ? val : "";

  return {
    id: String(userData.id || ""),
    name: getString(userData.name),
    email: getString(userData.email),
    role: getString(userData.role, "user"),

    age: getAge(userData.age),
    gender: getString(userData.gender),
    mobileNumber: getString(userData.mobileNumber),
    phone: getString(userData.phone),
    aadhaarNumber: getString(userData.aadhaarNumber || userData.aadhar),
    indianCdc: getString(userData.indianCdc || userData.cdc),
    rankDesignation: getString(userData.rankDesignation || userData.rank),
    currentShipName: getString(userData.currentShipName || userData.ship),
    currentShipImo: getString(userData.currentShipImo || userData.imo),
    profileImage: userData.profileImage ?? "",
    seafarerSid: userData.seafarerSid ?? "",

    stats: {
      courses: {
        completed: getNumber(
          userData.stats?.courses?.completed ??
            userData.stats?.coursesCompleted,
          5
        ),
        total: userData.stats?.courses?.total ?? 10,
      },
      quizzes: {
        completed: getNumber(
          userData.stats?.quizzes?.completed ?? userData.stats?.quizzesTaken,
          2
        ),
        total: userData.stats?.quizzes?.total ?? 10,
      },
      materials: {
        completed: getNumber(
          userData.stats?.materials?.completed ??
            userData.stats?.materialsViewed,
          5
        ),
        total: userData.stats?.materials?.total ?? 10,
      },
    },

    createdAt: getString(userData.createdAt, new Date().toISOString()),
    updatedAt: getString(userData.updatedAt, new Date().toISOString()),
    emailVerified: Boolean(userData.emailVerified),
    lastLogin: userData.lastLogin || null,
    // Preserve permissions from userData
    permissions: userData.permissions || undefined,
    _raw: userData,
    _sources: [source],
  };
}

/**
 * Merges user data from multiple sources
 * @param {...Array<UserData | NormalizedUser | null | undefined>} userDataObjects - Multiple user data objects to merge
 * @returns {Partial<NormalizedUser>} Merged user data
 */
export function mergeUserData(
  ...userDataObjects: Array<UserData | NormalizedUser | null | undefined>
): Partial<NormalizedUser> {
  return userDataObjects.reduce<Partial<NormalizedUser>>((merged, current) => {
    if (!current) return merged || {};

    const result: Partial<NormalizedUser> = { ...(merged || {}) };

    if (current.id !== undefined) result.id = String(current.id);
    if (current.name !== undefined) result.name = String(current.name);
    if (current.email !== undefined) result.email = String(current.email);
    if (current.role !== undefined) result.role = String(current.role);
    if (current.emailVerified !== undefined)
      result.emailVerified = Boolean(current.emailVerified);
    if (current.lastLogin !== undefined) result.lastLogin = current.lastLogin;
    if ("_raw" in current && typeof current._raw === "object") {
      result._raw = current._raw as UserData;
    }
    result.mobileNumber = current.mobileNumber ?? merged?.mobileNumber ?? "";
    const profileImage = current.profileImage || merged?.profileImage;
    const seafarerSid = current.seafarerSid || merged?.seafarerSid;
    result.age = current.age ?? merged?.age ?? "";
    result.gender = current.gender ?? merged?.gender ?? "";
    result.phone = current.phone ?? merged?.phone ?? "";
    result.aadhaarNumber = current.aadhaarNumber ?? merged?.aadhaarNumber ?? "";
    result.indianCdc = current.indianCdc ?? merged?.indianCdc ?? "";
    result.rankDesignation =
      current.rankDesignation ?? merged?.rankDesignation ?? "";
    result.currentShipName =
      current.currentShipName ?? merged?.currentShipName ?? "";
    result.currentShipImo =
      current.currentShipImo ?? merged?.currentShipImo ?? "";
    result.profileImage = profileImage ?? "";
    result.seafarerSid = seafarerSid ?? "";
    result.createdAt =
      current.createdAt ?? merged?.createdAt ?? new Date().toISOString();
    result.updatedAt =
      current.updatedAt ?? merged?.updatedAt ?? new Date().toISOString();

    result.stats = {
      courses: {
        completed:
          current.stats?.courses?.completed ??
          merged?.stats?.courses?.completed ??
          0,
        total:
          current.stats?.courses?.total ?? merged?.stats?.courses?.total ?? 0,
      },
      quizzes: {
        completed:
          current.stats?.quizzes?.completed ??
          merged?.stats?.quizzes?.completed ??
          0,
        total:
          current.stats?.quizzes?.total ?? merged?.stats?.quizzes?.total ?? 0,
      },
      materials: {
        completed:
          current.stats?.materials?.completed ??
          merged?.stats?.materials?.completed ??
          0,
        total:
          current.stats?.materials?.total ??
          merged?.stats?.materials?.total ??
          0,
      },
    };

    const mergedSources = (merged?._sources || []) as string[];
    const currentSources = (
      current && "_sources" in current ? current._sources || [] : []
    ) as string[];

    result._sources = [...new Set([...mergedSources, ...currentSources])];

    return result;
  }, {});
}

/**
 * Helper function to get a nested property with a default value
 * @param {any} user - User object to get the field from
 * @param {string} path - Dot notation path to the field
 * @param {T} defaultValue - Default value to return if the field doesn't exist
 * @returns {T} The value at the specified path or the default value
 */
export function getUserField<T>(
  user: Record<string, unknown> | undefined | null,
  path: string,
  defaultValue: T
): T {
  if (!user) return defaultValue;

  const parts = path.split(".");
  let value: unknown = user;

  for (const part of parts) {
    if (value === null || typeof value !== "object" || !(part in value)) {
      return defaultValue;
    }
    value = (value as Record<string, unknown>)[part];
  }

  return value !== undefined ? (value as T) : defaultValue;
}

/**
 * Helper to get display-ready user info
 * @param {any} user - User object to get display info from
 * @returns {UserDisplayInfo} Display-ready user info
 */
export function getUserDisplayInfo(
  user: UserData | NormalizedUser | null | undefined
): UserDisplayInfo {
  if (!user) {
    return {
      name: "Guest User",
      email: "",
      role: "user",
      profileImage: "",
      mobileNumber: "",
      aadhaarNumber: "",
      rank: "",
      ship: "",
      imo: "",
      cdc: "",
      seafarerSid: "",
      gender: "",
      age: "",
      phone: "",
      stats: {
        courses: 0,
        quizzes: 0,
        materials: 0,
      },
    };
  }

  const getField = <T>(path: string, defaultValue: T): T =>
    getUserField(user, path, defaultValue);
  const profileImage = getField("profileImage", "");
  const seafarerSid = getField("seafarerSid", "");
  return {
    name: user.name || "Guest User",
    email: user.email || "",
    role: user.role || "user",
    profileImage: profileImage ?? "",
    aadhaarNumber: getField("aadhaarNumber", ""),
    mobileNumber: getField("mobileNumber", ""),
    rank: getField("rankDesignation", ""),
    ship: getField("currentShipName", ""),
    imo: getField("currentShipImo", ""),
    cdc: getField("indianCdc", ""),
    seafarerSid: seafarerSid ?? "",
    gender: getField("gender", ""),
    age: getField("age", ""),
    phone: getField("phone", ""),
    stats: {
      courses: getField("stats.coursesCompleted", 0),
      quizzes: getField("stats.quizzesTaken", 0),
      materials: getField("stats.materialsViewed", 0),
    },
  };
}
